import React, { useEffect, useRef, useState } from 'react';
import { View, Pressable, ScrollView, Animated, Easing, useWindowDimensions, Platform } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useAppStore } from '../store/useAppStore';
import { useHospitalityStore } from '../store/useHospitalityStore';
import { useSim } from '../hooks/useSim';
import { bootstrapHospitality } from '../services/hospitalitySync';
import { signOut } from '../services/auth/auth';
import { handoffToPlatform } from '../lib/platformAuth';
import { seatedCount } from '../data/derive';
import { useTheme, TONE, SOFT, THEME_TOGGLE_ICON_D, type Tone } from '../theme';
import Icon from '../components/Icon';
import Avatar from '../components/Avatar';
import Txt from '../components/Txt';
import { NavProvider, type NavKey } from './NavContext';
import { PATHS, keyFromPath } from './routes';

import Dashboard from '../screens/Dashboard';
import Floor from '../screens/Floor';
import Reservations from '../screens/Reservations';
import Orders from '../screens/Orders';
import MenuSettings from '../screens/MenuSettings';
import Support from '../screens/Support';
import Guest from '../screens/Guest';
import Settings from '../screens/Settings';

/**
 * App shell — RN port of WebShell.tsx (which used react-router's <Outlet>).
 * Responsive: a permanent sidebar on wide viewports (web/tablet), an overlay drawer on
 * phones. Section switching is state-driven via NavContext (no react-router). The sim,
 * config bootstrap and toast host all mount here so they persist across every screen.
 */

interface NavEntry {
  key: NavKey;
  label: string;
  icon: string;
  badge?: string | null;
  badgeTone?: Tone;
}

const TITLES: Record<NavKey, [string, string]> = {
  dashboard: ['Live Ops', 'Real-time across SUKI, Calendar, Catalog & Billing'],
  floor: ['Floor & Tables', 'Tap a table to seat, combine or free · live from SK Calendar + SK Queue'],
  reservations: ['Reservations', 'Book, modify and cancel — SK Calendar prevents overbooking'],
  orders: ['Orders & Upsell', 'All open orders · search the full menu · deterministic upsell from the Catalog rule engine'],
  menu: ['Menu', 'Dishes, sections, labels & menus — stored in SK Catalog, published to Orders'],
  support: ['Support', 'Tickets resolved by SUKI, escalated to humans with full context'],
  guest: ['Guest experience', 'What the 200 guests see on their own phones'],
  settings: ['Settings', 'The only data sk-hospitality owns — config, not transactional state'],
};

const SCREENS: Record<NavKey, React.ComponentType> = {
  dashboard: Dashboard, floor: Floor, reservations: Reservations,
  orders: Orders, menu: MenuSettings, support: Support, guest: Guest, settings: Settings,
};

/** First letter of up to the first two words of a name, e.g. "Arvind Ramesh" → "AR". */
function initialsFrom(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  return parts.slice(0, 2).map((p) => p[0]).join('');
}

/** Pulsing "SUKI online" dot (replaces the CSS sk-pulse keyframe). */
function PulseDot({ color }: { color: string }) {
  const scale = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(scale, { toValue: 2.4, duration: 900, easing: Easing.out(Easing.ease), useNativeDriver: true }),
        Animated.timing(scale, { toValue: 1, duration: 0, useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [scale]);
  return (
    <View style={{ width: 7, height: 7, alignItems: 'center', justifyContent: 'center' }}>
      <Animated.View
        style={{ position: 'absolute', width: 7, height: 7, borderRadius: 3.5, backgroundColor: color, opacity: 0.5, transform: [{ scale }] }}
      />
      <View style={{ width: 7, height: 7, borderRadius: 3.5, backgroundColor: color }} />
    </View>
  );
}

/** Live wall-clock "HH:MM" for the header, re-rendering every second. */
function useNowClock(): string {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return String(now.getHours()).padStart(2, '0') + ':' + String(now.getMinutes()).padStart(2, '0');
}

function NavButton({ item, active, onPress }: { item: NavEntry; active: boolean; onPress: () => void }) {
  const t = useTheme();
  return (
    <Pressable
      onPress={onPress}
      style={{
        width: '100%',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 11,
        paddingVertical: 9,
        paddingHorizontal: 11,
        marginBottom: 2,
        borderRadius: 8,
        backgroundColor: active ? t.colors.accentSoft : 'transparent',
      }}
    >
      <Icon name={item.icon} size={17} strokeWidth={1.7} color={active ? t.colors.accentInk : t.colors.ink2} />
      <Txt size="md" weight="medium" color={active ? 'accentInk' : 'ink2'} style={{ flex: 1 }}>{item.label}</Txt>
      {item.badge && (
        <View style={{ minWidth: 18, height: 18, paddingHorizontal: 5, borderRadius: 9, alignItems: 'center', justifyContent: 'center', backgroundColor: SOFT[item.badgeTone ?? 'neutral'] }}>
          <Txt mono style={{ fontSize: 11, color: TONE[item.badgeTone ?? 'neutral'] }}>{item.badge}</Txt>
        </View>
      )}
    </Pressable>
  );
}

function NavGroup({ label, items, activeKey, onNavigate }: { label: string; items: NavEntry[]; activeKey: NavKey; onNavigate: (k: NavKey) => void }) {
  return (
    <View>
      <Txt upper color="ink4" style={{ fontSize: 10, letterSpacing: 0.8, paddingHorizontal: 10, paddingTop: 14, paddingBottom: 4 }}>{label}</Txt>
      {items.map((item) => (
        <NavButton key={item.key} item={item} active={item.key === activeKey} onPress={() => onNavigate(item.key)} />
      ))}
    </View>
  );
}

function Sidebar({ activeKey, onNavigate, navMain }: { activeKey: NavKey; onNavigate: (k: NavKey) => void; navMain: NavEntry[] }) {
  const t = useTheme();
  const { theme, toggleTheme, logout, displayName, email } = useAppStore();
  const venueName = useHospitalityStore((s) => s.location.name);
  const [accountOpen, setAccountOpen] = useState(false);

  const handleSignOut = async () => {
    setAccountOpen(false);
    await signOut();
    logout();
    handoffToPlatform('/');
  };

  return (
    <View style={{ width: 236, flexShrink: 0, backgroundColor: t.colors.card, borderRightWidth: 1, borderRightColor: t.colors.line }}>
      {/* Brand */}
      <View style={{ paddingHorizontal: 18, paddingTop: 18, paddingBottom: 14, borderBottomWidth: 1, borderBottomColor: t.colors.line }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <View style={{ width: 30, height: 30, borderRadius: 8, backgroundColor: t.colors.accent, alignItems: 'center', justifyContent: 'center' }}>
            <Txt weight="bold" style={{ color: '#fff', fontSize: 14 }}>SK</Txt>
          </View>
          <View>
            <Txt weight="semibold" style={{ fontSize: 14 }}>SK Hospitality</Txt>
            <Txt mono size="xs" color="ink3">{venueName || 'Set up your venue'}</Txt>
          </View>
        </View>
      </View>

      {/* Nav groups */}
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingHorizontal: 10, paddingVertical: 12 }}>
        <NavGroup label="Operator" items={navMain} activeKey={activeKey} onNavigate={onNavigate} />
        <NavGroup label="Guest-facing" items={[{ key: 'guest', label: 'Guest self-join', icon: 'phone' }]} activeKey={activeKey} onNavigate={onNavigate} />
        <NavGroup label="Configuration" items={[{ key: 'settings', label: 'Settings', icon: 'settings' }]} activeKey={activeKey} onNavigate={onNavigate} />
      </ScrollView>

      {/* Account footer */}
      <View style={{ position: 'relative' }}>
        {accountOpen && (
          <>
            <Pressable
              onPress={() => setAccountOpen(false)}
              style={{ position: 'absolute', bottom: '100%', left: -400, right: -400, height: 1200 }}
            />
            <View style={{ position: 'absolute', bottom: '100%', left: 8, right: 8, marginBottom: 6, backgroundColor: t.colors.card, borderWidth: 1, borderColor: t.colors.line, borderRadius: 12, overflow: 'hidden', ...t.shadows.lg }}>
              <View style={{ padding: 12, borderBottomWidth: 1, borderBottomColor: t.colors.line }}>
                <Txt size="md" weight="semibold">{displayName}</Txt>
                <Txt size="xs" color="ink3">{email || 'Floor manager'}</Txt>
              </View>
              <Pressable onPress={handleSignOut} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 9, paddingHorizontal: 14 }}>
                <Icon name="logout" size={14} color={t.colors.danger} strokeWidth={1.7} />
                <Txt size="md" color="danger">Sign out</Txt>
              </Pressable>
            </View>
          </>
        )}
        <View style={{ paddingVertical: 12, paddingHorizontal: 14, borderTopWidth: 1, borderTopColor: t.colors.line, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <Pressable onPress={() => setAccountOpen((v) => !v)} style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <Avatar initials={initialsFrom(displayName)} size="sm" background={t.colors.accent} color="#fff" />
            <View style={{ flex: 1 }}>
              <Txt size="sm" weight="medium">{displayName}</Txt>
              <Txt size="xs" color="ink3">Floor manager</Txt>
            </View>
          </Pressable>
          <Pressable onPress={toggleTheme} style={{ width: 26, height: 26, borderRadius: 5, borderWidth: 1, borderColor: t.colors.line, alignItems: 'center', justifyContent: 'center' }}>
            <Svg viewBox="0 0 24 24" width={14} height={14} fill="none">
              <Path d={THEME_TOGGLE_ICON_D} stroke={t.colors.ink2} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" />
            </Svg>
          </Pressable>
        </View>
      </View>
    </View>
  );
}

export default function AppShell() {
  const t = useTheme();
  const { width } = useWindowDimensions();
  const wide = width >= 900;
  const [activeKey, setActiveKey] = useState<NavKey>(() =>
    Platform.OS === 'web' && typeof window !== 'undefined' ? keyFromPath(window.location.pathname) : 'dashboard',
  );
  const [drawerOpen, setDrawerOpen] = useState(false);

  useSim();
  useEffect(() => { void bootstrapHospitality(); }, []);

  // Web only — RN native has no History API. Canonicalize an unrecognized path
  // (e.g. a stale /auth/magic-verify?token=… left over from the auth handoff)
  // to its tab's real URL, and follow browser back/forward between tabs.
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const resolvedPath = PATHS[keyFromPath(window.location.pathname)];
    if (window.location.pathname !== resolvedPath) {
      window.history.replaceState({}, '', resolvedPath);
    }
    const onPopState = () => setActiveKey(keyFromPath(window.location.pathname));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const tables = useHospitalityStore((s) => s.tables);
  const reservations = useHospitalityStore((s) => s.reservations);
  const ordersOnWay = useHospitalityStore((s) => s.ordersOnWay);
  const tickets = useHospitalityStore((s) => s.tickets);
  const nowClock = useNowClock(); // real wall-clock time (the store `clock` sim no longer ticks)

  const seated = seatedCount(tables);
  const pending = reservations.filter((r) => r.status === 'pending').length;
  const openTickets = tickets.filter((tk) => tk.status === 'needs_human').length;

  const navMain: NavEntry[] = [
    { key: 'dashboard', label: 'Live Ops', icon: 'home' },
    { key: 'floor', label: 'Floor & Tables', icon: 'grid', badge: `${seated}/${tables.length}`, badgeTone: 'orange' },
    { key: 'reservations', label: 'Reservations', icon: 'calendar', badge: pending ? String(pending) : null, badgeTone: 'warn' },
    { key: 'orders', label: 'Orders & Upsell', icon: 'receipt', badge: String(ordersOnWay), badgeTone: 'info' },
    { key: 'menu', label: 'Menu', icon: 'grid' },
    { key: 'support', label: 'Support', icon: 'bell', badge: openTickets ? String(openTickets) : null, badgeTone: 'danger' },
  ];

  const navigate = (k: NavKey) => {
    setActiveKey(k);
    setDrawerOpen(false);
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location.pathname !== PATHS[k]) {
      window.history.pushState({}, '', PATHS[k]);
    }
  };
  const [title, subtitle] = TITLES[activeKey];
  const ActiveScreen = SCREENS[activeKey];

  return (
    <NavProvider value={{ activeKey, navigate }}>
      <View style={{ flex: 1, flexDirection: 'row', backgroundColor: t.colors.paper }}>
        {wide && <Sidebar activeKey={activeKey} onNavigate={navigate} navMain={navMain} />}

        {/* Main column */}
        <View style={{ flex: 1, minWidth: 0 }}>
          {/* Header */}
          <View style={{ height: 58, flexDirection: 'row', alignItems: 'center', gap: 16, paddingHorizontal: 22, borderBottomWidth: 1, borderBottomColor: t.colors.line, backgroundColor: t.colors.card }}>
            {!wide && (
              <Pressable onPress={() => setDrawerOpen(true)} style={{ padding: 4 }}>
                <Icon name="grid" size={20} color={t.colors.ink2} />
              </Pressable>
            )}
            <View style={{ flex: 1 }}>
              <Txt size="lg" weight="semibold">{title}</Txt>
              <Txt size="sm" color="ink3" numberOfLines={1}>{subtitle}</Txt>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7, paddingVertical: 5, paddingHorizontal: 11, borderWidth: 1, borderColor: t.colors.successLine, backgroundColor: t.colors.successSoft, borderRadius: 999 }}>
              <PulseDot color={t.colors.success} />
              <Txt size="sm" weight="semibold" color="success">SUKI online</Txt>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <Icon name="clock" size={14} strokeWidth={1.6} color={t.colors.ink2} />
              <Txt mono size="md" color="ink2">{nowClock}</Txt>
            </View>
          </View>

          {/* Active screen */}
          <View style={{ flex: 1 }}>
            <ActiveScreen />
          </View>
        </View>

        {/* Narrow: overlay drawer */}
        {!wide && drawerOpen && (
          <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, flexDirection: 'row', zIndex: t.zIndex.overlay }}>
            <Sidebar activeKey={activeKey} onNavigate={navigate} navMain={navMain} />
            <Pressable onPress={() => setDrawerOpen(false)} style={{ flex: 1, backgroundColor: 'rgba(16,24,40,0.32)' }} />
          </View>
        )}
      </View>
    </NavProvider>
  );
}
